/** Points, bonus fighters and Supernova's chain (docs/spec/02 §2, §8; 03 §4). */

import { isDiving } from './roster';
import { CHALLENGE, chainMultiplier, NOVA, SCORE } from './tuning';
import type { Enemy, GameState, Mode } from './types';

/** The score that earns the bonus fighter after the one at `at` (0 for the first). Supernova's come later. */
export function nextLifeAfter(mode: Mode, at: number): number {
  const table = mode === 'supernova' ? NOVA : SCORE;
  for (const t of table.lifeAt) if (t > at) return t;
  return at + table.lifeEvery;
}

export function addScore(s: GameState, points: number): void {
  if (points <= 0) return;
  s.score += points;
  while (s.score >= s.nextLifeAt) {
    // With the reserve full there is nothing to give, and nothing is announced.
    if (s.lives < SCORE.maxLives) {
      s.lives++;
      s.events.push({ type: 'extraLife' });
    }
    s.nextLifeAt = nextLifeAfter(s.mode, s.nextLifeAt);
  }
}

/** What `e` is worth where it is now, before any multiplier. */
export function killPoints(s: GameState, e: Enemy): number {
  if (s.phase === 'challenge') return CHALLENGE.hit;
  const i = isDiving(e) ? 1 : 0;
  switch (e.kind) {
    case 'stinger': return SCORE.stinger[i];
    case 'moth': return SCORE.moth[i];
    case 'captive': return SCORE.captive[i];
    case 'morph': return SCORE.morph;
    case 'monarch': return i === 0 ? SCORE.monarch[0] : (SCORE.monarch[1 + Math.min(2, e.escorts)] ?? SCORE.monarch[1]);
  }
}

/** Count a kill toward the chain; the multiplier it earns (always 1 in Classic). */
export function chainKill(s: GameState): number {
  if (s.mode !== 'supernova') return 1;
  const before = chainMultiplier(s.chain);
  s.chain++;
  s.chainClock = NOVA.chainClock;
  if (s.chain > s.stats.bestChain) s.stats.bestChain = s.chain;
  const now = chainMultiplier(s.chain);
  if (now > before) s.events.push({ type: 'chain', level: now });
  return now;
}

/** The chain's clock never stops: a chain lives as long as the kills keep coming. */
export function tickChain(s: GameState): void {
  if (s.chain > 0 && --s.chainClock <= 0) s.chain = 0;
}
